// Documentation.js
import React, { useState } from 'react';
import '../../../../assests/css/admin/Help&Support/Documentation/Documentation.css';

const Documentation = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState('add-variants');
  const [isEditing, setIsEditing] = useState(false);

  const sections = [
    { id: 'add-variants', title: 'How to add variants' },
    { id: 'update-variants', title: 'Updating variant details' },
    { id: 'manage-inventory', title: 'Managing inventory' },
    { id: 'bulk-actions', title: 'Bulk actions' }
  ];

  const handleEdit = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      alert('Edit mode enabled. You can now modify the documentation.');
    } else {
      alert('Changes saved successfully!');
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    console.log('Scrolling to:', sectionId);
  };

  return (
    <div className="AdminDocs-container">
      <div className="AdminDocs-sidebar">
        <div className="AdminDocs-sidebar-header" onClick={onBack} style={{cursor: 'pointer'}}>
          ← Back to Support
        </div>
        <nav className="AdminDocs-nav">
          {sections.map(section => (
            <a 
              key={section.id}
              href={`#${section.id}`}
              className={`AdminDocs-nav-link ${activeSection === section.id ? 'AdminDocs-nav-link-active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(section.id);
              }}
            >
              {section.title}
            </a>
          ))}
        </nav>
      </div>

      <div className="AdminDocs-content">
        <button className="AdminDocs-edit-btn" onClick={handleEdit}>
          ✏️ {isEditing ? 'Save Changes' : 'Edit Page'}
        </button>
        
        <h1 className="AdminDocs-main-title">Adding a Product Variant</h1>
        
        <div className="AdminDocs-meta">
          <span>🕒 Last updated 2 days ago</span>
          <span>👁️ 1.2k views</span>
        </div>

        <p className="AdminDocs-intro">
          Product variants allow you to offer different versions of the same product, such as different sizes, colors, or materials. Each combination of options is a unique variant with its own inventory and pricing.
        </p>

        <div className="AdminDocs-key-concept">
          <div className="AdminDocs-key-concept-icon">ℹ️</div>
          <div>
            <strong>Key Concept</strong>
            <p>You can add up to 3 options for each product (e.g., Size, Color, and Style) and up to 100 variants per product.</p>
          </div>
        </div>

        <h2 className="AdminDocs-section-title" id="add-variants">How to add variants</h2>
        
        <p>To add variants to a product, navigate to the product details page and locate the 'Variants' section. You can add options like size, color, or material. Each variant can have its own price, SKU, and inventory level. Follow these steps:</p>

        <div className="AdminDocs-step">
          <div className="AdminDocs-step-number">1</div>
          <div className="AdminDocs-step-content">
            <p>From your admin dashboard, go to <strong>Products</strong> and click on the product name you want to edit.</p>
          </div>
        </div>

        <div className="AdminDocs-step">
          <div className="AdminDocs-step-number">2</div>
          <div className="AdminDocs-step-content">
            <p>Scroll down to the <strong>Variants</strong> section and click <strong>Add options like size or color</strong>.</p>
          </div>
        </div>

        <div className="AdminDocs-step">
          <div className="AdminDocs-step-number">3</div>
          <div className="AdminDocs-step-content">
            <p>Enter the Option name (e.g., Size) and Option values (e.g., Small, Medium, Large).</p>
          </div>
        </div>

        <div className="AdminDocs-step">
          <div className="AdminDocs-step-number">4</div>
          <div className="AdminDocs-step-content">
            <p>Click <strong>Save</strong> to apply the variants. Each combination will automatically generate a new variant entry.</p>
          </div>
        </div>

        <div className="AdminDocs-pro-tip">
          <div className="AdminDocs-pro-tip-icon">💡</div>
          <div>
            <strong>Pro Tip</strong>
            <p>Use the 'Bulk Upload' feature if you need to add hundreds of variants at once via CSV. This can save you hours of manual data entry.</p>
          </div>
        </div>

        <h2 className="AdminDocs-section-title" id="update-variants">Updating variant details</h2>
        <p>You can modify individual variant properties at any time. Click on any variant in the list to edit its price, SKU, inventory quantity, or other attributes. Changes are saved automatically.</p>

        <h2 className="AdminDocs-section-title" id="manage-inventory">Managing inventory</h2>
        <p>Each variant has its own inventory count. You can track stock levels, set low stock alerts, and manage inventory across multiple locations. The system will automatically update availability based on your inventory settings.</p>

        <h2 className="AdminDocs-section-title" id="bulk-actions">Bulk actions</h2>
        <p>To perform actions on multiple variants simultaneously, select the variants you want to modify using the checkboxes, then choose an action from the bulk actions menu. Available actions include updating prices, changing inventory, or deleting variants.</p>
      </div>

      <div className="AdminDocs-toc">
        <h3 className="AdminDocs-toc-title">On this page</h3>
        <ul className="AdminDocs-toc-list">
          {sections.map(section => (
            <li key={section.id}>
              <a 
                href={`#${section.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(section.id);
                }}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Documentation;